/**
 * Geokodowanie przez Supabase Edge Function `geocode-address`
 * (cache DB + Nominatim po stronie serwera). Nie wywołuje Nominatim z przeglądarki.
 */
import { supabase } from '../lib/supabase';
import {
  GEOCODE_NOMINATIM_COUNTRYCODES,
  isGeocodeCountryFilterActive,
  isLatLngRoughlyInPoland,
} from '../config/geocodeRegion';

async function invokeGeocode(body) {
  const payload = { ...(body && typeof body === 'object' ? body : {}), countrycodes: GEOCODE_NOMINATIM_COUNTRYCODES };
  const { data, error } = await supabase.functions.invoke('geocode-address', {
    body: payload,
  });

  if (error) {
    throw error;
  }
  if (data && typeof data === 'object' && data.error) {
    throw new Error(data.error);
  }
  return data;
}

/**
 * @param {string} address
 * @returns {Promise<{ lat: number, lng: number, address?: string, cached?: boolean } | null>}
 */
export async function resolveAddress(address) {
  const q = (address || '').trim();
  if (!q) return null;
  try {
    const data = await invokeGeocode({ action: 'resolve', address: q });
    if (data?.lat == null || data?.lng == null) return null;
    const lat = Number(data.lat);
    const lng = Number(data.lng);
    if (isGeocodeCountryFilterActive() && !isLatLngRoughlyInPoland(lat, lng)) {
      return null;
    }
    return {
      lat,
      lng,
      address: data.address,
      cached: data.cached,
    };
  } catch {
    /* resolve 404/502 — spróbuj pierwszego wyniku search (np. niepełna nazwa miasta) */
    try {
      const { predictions } = await searchAddresses(q);
      const p = predictions?.[0];
      if (p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lon))) {
        const lat = Number(p.lat);
        const lng = Number(p.lon);
        if (isGeocodeCountryFilterActive() && !isLatLngRoughlyInPoland(lat, lng)) {
          return null;
        }
        return {
          lat,
          lng,
          address: p.description,
          cached: false,
        };
      }
    } catch {
      /* ignore */
    }
    return null;
  }
}

/**
 * Autocomplete — ten sam kształt co geoSearchService.autocomplete
 * @param {string} input
 * @param {object} _options zachowane dla kompatybilności API
 */
export async function searchAddresses(input, _options = {}) {
  const q = (input || '').trim();
  if (q.length < 2) {
    return { predictions: [] };
  }
  try {
    const data = await invokeGeocode({ action: 'search', q });
    let predictions = data?.predictions || [];
    if (isGeocodeCountryFilterActive() && Array.isArray(predictions)) {
      predictions = predictions.filter(
        (p) => p && isLatLngRoughlyInPoland(p.lat, p.lon != null ? p.lon : p.lng)
      );
    }
    return { predictions };
  } catch {
    return { predictions: [] };
  }
}

/**
 * Lookup miejsca po place_id / osm_ids
 */
export async function lookupPlace(placeId) {
  const data = await invokeGeocode({ action: 'lookup', placeId: String(placeId) });
  if (data?.lat == null || data?.lng == null) {
    throw new Error('Lookup failed');
  }
  const lat = Number(data.lat);
  const lng = Number(data.lng);
  if (isGeocodeCountryFilterActive() && !isLatLngRoughlyInPoland(lat, lng)) {
    throw new Error('Lookup outside allowed region');
  }
  return {
    formatted_address: data.formatted_address || `${lat}, ${lng}`,
    geometry: {
      location: {
        lat: () => lat,
        lng: () => lng,
      },
    },
    place_id: null,
  };
}
